// src/components/dashboard/DashboardAudit.tsx
//
// UPDATED (this pass) — TrackRecordEntry's local interface brought in
// sync with the real backend shape after this session's verification/
// settlement decoupling:
//   - counterpartyOrigin union now includes 'viaOrganizationCredential'
//     — confirmed live and reachable, not hypothetical: a real smoke-
//     tested track record entry has this exact origin. The old two-
//     value union meant ORIGIN_LABELS[entry.counterpartyOrigin] would
//     silently return undefined and render a blank pill for any such
//     entry — a real bug against real data, not a stale comment.
//   - stagesSummary.negotiatedAt and .fulfilledAt REMOVED — neither
//     field has ever been written by any tool in this codebase (see
//     _trackRecord.ts's header for the full reasoning); displaying
//     them as permanent dashes misrepresented "never tracked" as
//     "tracked, but empty."
//   - stagesSummary.settledAt RENAMED to verifiedAt, and the header
//     copy updated to match: this record is written at FDW's
//     independent verification of completion, not at Stripe
//     settlement. Settlement is now a separate, optional, later fact
//     that may never occur — see this session's strategic scope
//     decision (FDW's product is verification, not settlement).
//
// UPDATED — outcomeConfirmation can now genuinely be populated, via
// confirm-engagement-outcome.ts (real source now reviewed). The type
// as declared in _trackRecord.ts (outcomeConfirmation: null, fixed) is
// out of date against what confirm-engagement-outcome.ts actually
// writes — { confirmed, note, confirmedAt, confirmedBy }. Typed here
// against the real write shape, not the stale declared type.
//
// FLAGGED, NOT SILENTLY FIXED: confirm-engagement-outcome.ts checks
// entry.data().counterpartyId against the resolved caller identity, but
// TrackRecordEntry's declared shape (_trackRecord.ts) has no
// counterpartyId field — only counterpartyOrigin. Either the tool has a
// real bug (checking a field that doesn't exist on written entries), or
// the actual Firestore documents carry more fields than the TS interface
// declares. This page defensively treats counterpartyId as optional/
// unknown rather than assuming its presence, but does not attempt to
// resolve the underlying mismatch — that's a backend correctness
// question, not a frontend one.
//
// NOTE — the comment above is itself now stale: _trackRecord.ts's real,
// current TrackRecordEntry DOES include counterpartyId (added earlier
// this session specifically so confirm-engagement-outcome.ts's identity
// check has something real to compare against). Left as historical
// context rather than deleted, since it documents a real investigation
// that was correct at the time it was written — but counterpartyId is
// no longer an open mismatch as of this pass.
//
// Empty state remains the expected default: trackRecordEntries only
// gets a write once an engagement is independently verified as
// complete by FDW (_trackRecord.ts's single write path, called from
// verifyOutcomeCompletion). Every outcome/kit built and tested so far
// in this build is draft-only or unverified and produces zero entries.
//
// MIGRATED — local FDW_BASE / dashboardToken / getDashboardToken /
// authedFetch block removed in favor of the shared dashboardApi client.
// Call site updated: '/dashboard/audit' → '/audit' (data route, now
// under /api). This was the exact class of bug the original outcomes
// route rewrite collision was meant to fix — '/dashboard/audit' as an
// exact-string rewrite collided with the '/dashboard/audit' page route
// itself before the /api prefix migration; this file just hadn't been
// updated to use the new path yet.
import React, { useState, useEffect } from 'react';
import { authedFetch } from '../../services/dashboardApi';

interface OutcomeConfirmation {
  confirmed: boolean;
  note: string | null;
  confirmedAt: string;
  confirmedBy: string;
}

interface TrackRecordEntry {
  entityUid: string;
  engagementRef: string;
  counterpartyOrigin: 'viaAssistantAuth' | 'viaA2ARegistration' | 'viaOrganizationCredential';
  stagesSummary: {
    committedAt: string | null;
    verifiedAt: string;
  };
  outcomeConfirmation: OutcomeConfirmation | null;
  createdAt: string;
  source: 'system';
}

const ORIGIN_LABELS: Record<TrackRecordEntry['counterpartyOrigin'], string> = {
  viaAssistantAuth: 'Via assistant',
  viaA2ARegistration: 'Agent-to-agent',
  viaOrganizationCredential: 'Another FDW business',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export const DashboardAudit: React.FC = () => {
  const [entries, setEntries] = useState<TrackRecordEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await authedFetch('/audit');
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to load your track record.');
        }
        const data = await res.json();
        if (!cancelled) setEntries(data.entries ?? []);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load your track record.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading your track record…</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-brand-black">Audit & proof</h1>
        <p className="text-sm text-brand-charcoal font-light mt-1">
          A system-written record of engagements FDW has independently verified as complete. You can't edit this — it's written automatically, once, at verification.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {entries.length === 0 && !error && (
        <div className="rounded-xl border border-dashed border-brand-border bg-brand-surface p-10 text-center">
          <p className="text-sm text-brand-charcoal font-light">
            Nothing here yet. This page fills in the first time FDW independently verifies a real engagement is complete — draft outcomes and packages you're testing don't create a record until fulfillment is genuinely checked.
          </p>
        </div>
      )}

      {entries.length > 0 && (
        <ul className="divide-y divide-brand-border rounded-xl border border-brand-border bg-brand-white">
          {entries.map((entry, i) => (
            <li key={`${entry.engagementRef}-${i}`} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-brand-black font-mono">{entry.engagementRef}</span>
                <span className="rounded-full px-2 py-0.5 text-xs font-mono bg-brand-surface text-brand-gray">
                  {ORIGIN_LABELS[entry.counterpartyOrigin]}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                <div>
                  <p className="text-brand-gray font-mono uppercase tracking-wide mb-0.5">Committed</p>
                  <p className="text-brand-charcoal">{formatDate(entry.stagesSummary.committedAt)}</p>
                </div>
                <div>
                  <p className="text-brand-gray font-mono uppercase tracking-wide mb-0.5">Verified</p>
                  <p className="text-brand-charcoal">{formatDate(entry.stagesSummary.verifiedAt)}</p>
                </div>
              </div>

              {entry.outcomeConfirmation ? (
                <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2">
                  <p className="text-xs font-medium text-green-700">
                    Confirmed by counterparty · {formatDate(entry.outcomeConfirmation.confirmedAt)}
                  </p>
                  {entry.outcomeConfirmation.note && (
                    <p className="text-xs text-green-800 mt-1 italic">"{entry.outcomeConfirmation.note}"</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-brand-gray italic">
                  No outcome confirmation from the counterparty yet.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};